import { type TabItem, Tabs } from "@aurem/ui";
import { useBreadcrumbs } from "components/Topbar/PageTitle";
import {
  companyDashboard,
  EmployeeParams,
  employees,
  ROUTES,
} from "const/routes";
import { useAuthorize } from "graphql/user";
import { useCompanyName } from "hooks/useCompanyName";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { PermissionEnum } from "types";
import { PageLayout } from "../../../../../../layouts/PageLayout";
import { useEmployeeName } from "../hooks/get-employees.hooks";

export const EmployeeDetailsLayout = () => {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { companyId, employeeId } = useParams() as EmployeeParams;

  const { firstName, lastName } = useEmployeeName(employeeId);

  const beneficiariesPermitted = useAuthorize(
    PermissionEnum.ViewEmployeeNominees,
  );
  const tabItems: TabItem[] = useMemo(() => {
    const paths: TabItem[] = [
      {
        id: "personal-details",
        text: t(
          "apps.admin.pages.companies.companyId.employees.employeeId.page.navigation.personalDetails",
        ),
      },
      {
        id: "employment-details",
        text: t(
          "apps.admin.pages.companies.companyId.employees.employeeId.page.navigation.employmentDetails",
        ),
      },
      {
        id: "products",
        text: t(
          "apps.admin.pages.companies.companyId.employees.employeeId.page.navigation.products",
        ),
      },
    ];
    if (beneficiariesPermitted) {
      paths.push({
        id: "beneficiaries",
        text: t(
          "apps.admin.pages.companies.companyId.employees.employeeId.page.navigation.beneficiaries",
        ),
      });
    }
    return paths;
  }, [t, beneficiariesPermitted]);

  const activeTab =
    tabItems.find((tab) => pathname.includes(tab.id))?.id || tabItems[0].id;

  const onTabChange = useCallback(
    (id: string) => {
      void navigate(id);
    },
    [navigate],
  );

  const employeeFullName = `${firstName} ${lastName}`.trim();
  const companyName = useCompanyName(companyId);

  useBreadcrumbs([
    { label: t("apps.admin.navBar.companies"), href: `/${ROUTES.COMPANIES}` },
    {
      label: companyName || "...",
      href: companyDashboard({ companyId }),
    },
    {
      label: t("apps.admin.common.employees"),
      href: employees({ companyId }),
    },
    { label: employeeFullName || "..." },
  ]);

  return (
    <PageLayout className="gap-6">
      <Tabs items={tabItems} activeItem={activeTab} onChange={onTabChange} />
      <Outlet />
    </PageLayout>
  );
};
